import { Component, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Last-resort catch for an uncaught render error anywhere below it. Without this,
 * a throw during render (e.g. a locally-persisted incident whose shape predates a
 * later ITASC field) unmounts the whole React tree to a blank white page with
 * nothing in the UI to say why — the failure only shows up in the browser console,
 * which most officers never open. This never replaces fixing the underlying throw;
 * it only stops one bad incident record from taking the whole app down silently.
 *
 * "Clear local incident data" is the actual recovery path for the case this exists
 * for: a persisted incident that crashes the deterministic rule engine on every
 * load. It only touches this app's own localStorage keys.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }): void {
    // eslint-disable-next-line no-console
    console.error('Unhandled error rendering the app:', error, info.componentStack);
  }

  private clearLocalDataAndReload = (): void => {
    try {
      for (const key of Object.keys(window.localStorage)) {
        if (key.startsWith('duty.')) window.localStorage.removeItem(key);
      }
    } catch {
      /* storage unavailable — reload still gives a clean in-memory start */
    }
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface px-4 text-text">
        <div className="max-w-md rounded-xl border border-border bg-surface-2 p-6 text-center">
          <AlertTriangle className="mx-auto mb-3 text-crit" size={28} />
          <h1 className="text-base font-semibold">Duty hit a problem it couldn't recover from</h1>
          <p className="mt-2 text-sm text-muted">
            Something on this device — most likely a saved incident from an earlier version —
            caused the app to fail while loading. Your other saved incidents are not affected by
            this screen.
          </p>
          <div className="mt-5 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-md border border-border px-3 py-2 text-sm hover:border-accent/50 hover:text-accent"
            >
              Reload
            </button>
            <button
              type="button"
              onClick={this.clearLocalDataAndReload}
              className="rounded-md border border-crit/40 px-3 py-2 text-sm text-crit hover:bg-crit/10"
            >
              Clear local incident data and reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}
